import moment from 'moment'
import {
  EmployeeTransitPass,
  EmployeeTransitPassStatus,
} from 'redux/transitPass/transitPassTypes'

export const OPEN_TRANSIT_CARD_STATUSES: EmployeeTransitPassStatus[] = [
  'REQUESTED',
  'ORDERED',
  'VERIFYING',
  'ACTIVE',
]

export const isOpenTransitCardStatus = (
  status: EmployeeTransitPassStatus
): boolean => OPEN_TRANSIT_CARD_STATUSES.includes(status)

export const getOpenTransitAgencyIds = (
  transitPasses?: EmployeeTransitPass[] | null
): Set<string> => {
  const ids = new Set<string>()
  ;(transitPasses || []).forEach((pass) => {
    if (isOpenTransitCardStatus(pass.status) && pass.transitAgency) {
      ids.add(pass.transitAgency)
    }
  })
  return ids
}

export const areAllRequestableAgenciesBlocked = (
  agencies?: { id: string }[] | null,
  transitPasses?: EmployeeTransitPass[] | null
): boolean => {
  if (!agencies?.length) {
    return true
  }
  const blocked = getOpenTransitAgencyIds(transitPasses)
  return agencies.every((agency) => blocked.has(agency.id))
}

export const getFirstAvailableAgencyId = (
  agencies?: { id: string }[] | null,
  transitPasses?: EmployeeTransitPass[] | null
): string => {
  const blocked = getOpenTransitAgencyIds(transitPasses)
  return agencies?.find((agency) => !blocked.has(agency.id))?.id || ''
}

export const getTransitCardTitle = (pass: EmployeeTransitPass): string =>
  `${pass.transitAgencyName || 'Transit'} Card`

export const sortTransitPasses = (
  transitPasses?: EmployeeTransitPass[] | null
): EmployeeTransitPass[] => {
  return [...(transitPasses || [])].sort((a, b) => {
    const nameA = (a.transitAgencyName || '').toLowerCase()
    const nameB = (b.transitAgencyName || '').toLowerCase()
    if (nameA !== nameB) {
      return nameA.localeCompare(nameB)
    }
    return a.createdAt.localeCompare(b.createdAt)
  })
}

export const formatTransitPassNumber = (
  transitPassNumber?: string | null
): string => {
  if (!transitPassNumber) {
    return 'Pending'
  }
  const digits = transitPassNumber.replace(/\s+/g, '')
  if (!/^\d+$/.test(digits) || digits.length % 4 !== 0) {
    return transitPassNumber
  }
  return digits.match(/.{1,4}/g)?.join(' ') || transitPassNumber
}

export const getTransitCardStatusLabel = (
  status: EmployeeTransitPassStatus
): string => {
  if (status === 'ACTIVE') {
    return 'Assigned'
  }
  if (status === 'REQUESTED') {
    return 'Requested'
  }
  if (status === 'ORDERED') {
    return 'Ordered'
  }
  if (status === 'VERIFYING') {
    return 'Verifying'
  }
  return 'Deactivated'
}

export const getTransitCardStatusColor = (
  status: EmployeeTransitPassStatus
): 'green' | 'yellow' | 'blue' | 'gray' => {
  if (status === 'ACTIVE') {
    return 'green'
  }
  if (status === 'REQUESTED' || status === 'ORDERED') {
    return 'yellow'
  }
  if (status === 'VERIFYING') {
    return 'blue'
  }
  return 'gray'
}

export const getTransitCardDateLabel = (
  pass: EmployeeTransitPass
): string | null => {
  if (pass.linkedDate) {
    return `Assigned ${moment.utc(pass.linkedDate).format('MMM D, YYYY')}`
  }
  if (pass.requestedDate) {
    return `Requested ${moment.utc(pass.requestedDate).format('MMM D, YYYY')}`
  }
  return null
}
